import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import type { ComponentProps } from "react";

import {
  getPageTitle,
  getPageTitleHref,
  shouldShowDefaultPageTitle,
} from "@src/features/app/top-nav/pageTitle";
import BuilderHeaderBackButton from "@src/features/app/top-nav/BuilderHeaderBackButton";
import { useDefaultPageTitleHidden } from "@src/features/app/top-nav/useDefaultPageTitleHidden";
import {
  getBuilderBackHref,
  getBuilderDomain,
  getStorageKey,
  isBuilderView,
  isTrackableListView,
  getBuilderInternalBackHref,
} from "@src/features/app/top-nav/builderNav";
import { cn } from "@src/lib/utils/cn";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import { useHasChildren } from "@src/features/app/top-nav/useHasChildren";

type LinkHref = ComponentProps<typeof Link>["href"];

export const PAGE_TITLE_CLASS =
  "truncate text-xl-v4 font-semibold text-text-primary";
export const BREADCRUMB_LINK_CLASS =
  "truncate rounded text-s-v4 font-medium-v4 text-text-secondary transition-colors hover:text-text-primary";

const PageContentHeaderZone: React.FC = () => {
  const router = useRouter();
  const [mainTabsRef, hasMainTabs] = useHasChildren();
  const [hasPageTitleOverride, setHasPageTitleOverride] = React.useState(false);
  const [rememberedBuilderBackHref, setRememberedBuilderBackHref] =
    React.useState<LinkHref | null>(null);
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  const taskLibraryItemTypeEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const operationsBundle = !auditsEnabled && !taskLibraryItemTypeEnabled;
  const isBuilder = isBuilderView(router.pathname, router.query);
  const pageTitle = getPageTitle(
    router.pathname,
    router.query,
    operationsBundle,
  );
  const pageTitleHref = getPageTitleHref(router.pathname, router.query);
  const builderBackFallbackHref = isBuilder
    ? getBuilderBackHref(router.pathname, router.query)
    : null;
  const builderInternalBackHref = isBuilder
    ? getBuilderInternalBackHref(router.pathname, router.query)
    : null;
  const builderBackHref =
    builderInternalBackHref ||
    rememberedBuilderBackHref ||
    builderBackFallbackHref;
  const titleHiddenByPage = useDefaultPageTitleHidden();
  const showDefaultTitle =
    !isBuilder &&
    !hasPageTitleOverride &&
    !titleHiddenByPage &&
    shouldShowDefaultPageTitle(router.pathname);

  React.useEffect(() => {
    const target = document.getElementById("top-nav-page-title-override");
    if (!target) return;

    const syncOverrideState = () => {
      setHasPageTitleOverride(target.childNodes.length > 0);
    };
    syncOverrideState();

    const observer = new MutationObserver(syncOverrideState);
    observer.observe(target, { childList: true });
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!router.isReady) return;

    const domain = getBuilderDomain(router.pathname);

    if (isTrackableListView(router.pathname, router.query) && domain) {
      window.sessionStorage.setItem(getStorageKey(domain), router.asPath);
      setRememberedBuilderBackHref(null);
      return;
    }

    if (isBuilder && domain) {
      const storedBackHref = window.sessionStorage.getItem(
        getStorageKey(domain),
      );
      setRememberedBuilderBackHref(
        storedBackHref ? (storedBackHref as LinkHref) : null,
      );
    }
  }, [isBuilder, router.asPath, router.isReady, router.pathname, router.query]);

  if (router.pathname === "/" && !isBuilder && !builderBackHref) {
    return null;
  }

  if (router.pathname.startsWith("/library/folders")) {
    return null;
  }

  if (router.pathname.startsWith("/settings")) {
    return null;
  }

  if (router.pathname.startsWith("/guest-feedback/")) {
    return null;
  }

  if (
    router.pathname.startsWith("/messages") ||
    router.pathname.startsWith("/newsfeed")
  ) {
    return null;
  }

  if (router.pathname.startsWith("/agents")) {
    return null;
  }

  return (
    <div
      className={cn(
        "@container/page-header-v1 shrink-0 px-2 md:px-4",
        isBuilder ? "border-b border-border-primary py-2" : "py-3",
      )}
    >
      <div
        className={cn(
          "min-w-0 items-center",
          // Same layout as PageContentHeaderZoneV2: a real centred column for
          // the tabs instead of an absolutely placed group, which ran under
          // the title and actions on a narrow header (the agent panel open on
          // a laptop). This header's actions run wider (up to ~385px), so the
          // tabs drop their labels below 55rem and move to their own row
          // below 45rem, each keeping ~140px for the title.
          hasMainTabs
            ? "grid grid-cols-[minmax(0,1fr)_auto_minmax(max-content,1fr)] gap-8 @max-[45rem]/page-header-v1:grid-cols-[minmax(0,1fr)_max-content] @max-[45rem]/page-header-v1:gap-x-4 @max-[45rem]/page-header-v1:gap-y-2"
            : "flex justify-between gap-4",
        )}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex min-w-0 items-center gap-2">
            <div
              id="top-nav-page-title-leading"
              className="flex items-center gap-1.5 empty:hidden"
            >
              {isBuilder && builderBackHref ? (
                <BuilderHeaderBackButton
                  href={builderBackHref}
                  replace={builderInternalBackHref != null}
                />
              ) : null}
            </div>
            {!isBuilder && (
              <div
                id="top-nav-page-title-override"
                className="flex min-w-0 items-center empty:hidden"
              />
            )}
            {showDefaultTitle &&
              (pageTitleHref ? (
                <Link href={pageTitleHref} className={BREADCRUMB_LINK_CLASS}>
                  {pageTitle}
                </Link>
              ) : (
                <h1 className={PAGE_TITLE_CLASS}>{pageTitle}</h1>
              ))}
            <div
              id="top-nav-page-title-extra"
              className={cn(
                "flex min-w-0 items-center gap-1.5",
                isBuilder ? "items-start" : "items-center",
              )}
            />
          </div>
          <div id="top-nav-page-subtitle" className="min-w-0 empty:hidden" />
        </div>
        <div
          id="top-nav-main-tabs"
          ref={mainTabsRef}
          className="flex min-w-0 items-center justify-center empty:hidden @max-[45rem]/page-header-v1:col-span-2 @max-[45rem]/page-header-v1:row-start-2 @max-[45rem]/page-header-v1:justify-start"
        />
        <div
          id="top-nav-actions"
          className="flex shrink-0 items-center justify-end gap-1.5 empty:hidden"
        />
      </div>
    </div>
  );
};

export default PageContentHeaderZone;
